import moment from 'moment'
import momentTimezone from 'moment-timezone'
import { dateFormat, getLocalizedDate, timeFormat } from 'commons/date'
import { useIntl } from 'react-intl'
import { usePlace } from './usePlace'
import * as R from 'ramda'

export default function useMessageExpiration(notification) {
  const { formatMessage } = useIntl()
  const place = usePlace()

  const hasExpiration = R.compose(
    R.not,
    R.isNil,
    R.prop('expirationDate'),
  )(notification)

  const _hasExpired = hasExpired(notification, place)

  return {
    id: R.prop('id', notification),
    hasExpiration,
    hasExpired: _hasExpired,
    expirationDate: getExpirationDate(notification, place, formatMessage),
    expirationDatePreview: getExpirationDatePreview(
      notification,
      place,
      formatMessage,
    ),
  }
}

export function createMoment(date, timeZone) {
  return R.isNil(timeZone) ? moment(date) : momentTimezone(date).tz(timeZone)
}

const hasExpired = (notification, place) =>
  R.compose(
    R.propSatisfies(
      R.allPass([
        R.complement(R.isNil),
        (expirationDate) => {
          const placeTimeZone = R.path(['info', 'timezone'], place)
          const date = createMoment(expirationDate, placeTimeZone)
          const now = createMoment(undefined, placeTimeZone)
          return date.isBefore(now)
        },
      ]),
      'expirationDate',
    ),
  )(notification)

const getExpirationDate = (notification, place, formatMessage) => {
  const expirationDate = R.prop('expirationDate', notification)
  if (!expirationDate) return undefined

  const placeTimeZone = R.path(['info', 'timezone'], place)
  const date = createMoment(expirationDate, placeTimeZone)
  const time = date.format(timeFormat)

  return formatMessage(
    {
      id: hasExpired(notification, place)
        ? 'notificationTimeline.card.messageExpiration.hasExpired'
        : 'notificationTimeline.card.messageExpiration.willExpire',
    },
    { time, date: date.format(dateFormat) },
  )
}

const getExpirationDatePreview = (notification, place, formatMessage) => {
  const expirationDate = R.prop('expirationDate', notification)
  if (!expirationDate) return undefined

  const placeTimeZone = R.path(['info', 'timezone'], place)
  const date = createMoment(expirationDate, placeTimeZone)

  const formattedDate = getLocalizedDate(date, 'DATE')
  const formattedTime = getLocalizedDate(date, 'TIME')

  return formatMessage(
    {
      id: 'createNotification.dialog.messageExpiration.preview.willExpire',
    },
    {
      date: formattedDate,
      time: formattedTime,
    },
  )
}
